import React, { useEffect, useRef, useState } from 'react'
import {
  XMarkIcon,
  StopIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatBytes, formatTradingDay, formatRecordTimeRange, ImportErrorCount, parseImportErrorDetails } from './dailyDataUtils'

function fileResultLabel(r) {
  if (r.status === 'pending') return 'Waiting'
  if (r.status === 'running') return 'Importing…'
  if (r.status === 'error') return 'Failed'
  if (r.status === 'stopped') return 'Stopped'
  if (r.data?.errors > 0 && r.data?.imported === 0) return 'Failed'
  if (r.data?.errors > 0) return 'Done (warnings)'
  return 'Done'
}

function FileStatusIcon({ row, size = 'md' }) {
  const cls = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5 flex-shrink-0'
  if (row.status === 'running') {
    return <ArrowPathIcon className={`${cls} text-primary-600 animate-spin`} aria-hidden />
  }
  if (row.status === 'done') {
    const failed = row.data?.errors > 0 && row.data?.imported === 0
    const warned = row.data?.errors > 0
    if (failed) return <XCircleIcon className={`${cls} text-red-600`} aria-hidden />
    if (warned) return <CheckCircleIcon className={`${cls} text-amber-500`} aria-hidden />
    return <CheckCircleSolidIcon className={`${cls} text-green-600`} aria-hidden />
  }
  if (row.status === 'error') return <XCircleIcon className={`${cls} text-red-600`} aria-hidden />
  if (row.status === 'stopped') return <MinusCircleIcon className={`${cls} text-gray-400`} aria-hidden />
  return <span className={`${cls} inline-block rounded-full border-2 border-gray-300`} aria-hidden />
}

function ProgressDot({ row, index }) {
  let dotCls = 'bg-gray-200 border-gray-300'
  let content = null
  if (row.status === 'running') {
    dotCls = 'bg-primary-100 border-primary-500 ring-2 ring-primary-300 animate-pulse'
  } else if (row.status === 'done') {
    const failed = row.data?.errors > 0 && row.data?.imported === 0
    const warned = row.data?.errors > 0
    if (failed) dotCls = 'bg-red-100 border-red-500'
    else if (warned) dotCls = 'bg-amber-100 border-amber-500'
    else dotCls = 'bg-green-100 border-green-500'
    content = <span className="text-[9px] leading-none text-green-700">✓</span>
  } else if (row.status === 'error') {
    dotCls = 'bg-red-100 border-red-500'
    content = <span className="text-[9px] leading-none text-red-700">✕</span>
  } else if (row.status === 'stopped') {
    dotCls = 'bg-gray-100 border-gray-400'
  }

  return (
    <div
      title={`${index + 1}. ${row.name} — ${fileResultLabel(row)}`}
      className={`w-5 h-5 rounded-full border flex items-center justify-center ${dotCls}`}
    >
      {content}
    </div>
  )
}

function buildFileErrors(rows) {
  return (rows || [])
    .filter((r) => r.data?.errors > 0 || r.error)
    .map((r) => ({
      name: r.name,
      blob_path: r.blob_path,
      imported: r.data?.imported,
      updated: r.data?.updated,
      skipped: r.data?.skipped,
      errors: r.data?.errors || (r.error ? 1 : 0),
      error_message: r.error || r.data?.error_message,
      error_details: r.data?.error_details,
    }))
}

export default function BatchIngestProgressModal({
  open,
  files,
  tradingDay,
  folderName,
  templateId,
  apiPath = '/api/daily-data/patron/run',
  folderPath = null,
  venueMappings = null,
  onClose,
  onComplete,
  onViewErrors,
}) {
  const stopRef = useRef(false)
  const startedRef = useRef(false)
  const [phase, setPhase] = useState('idle') // idle | running | stopped | done
  const [stopRequested, setStopRequested] = useState(false)
  const [rows, setRows] = useState([])
  const [currentIdx, setCurrentIdx] = useState(-1)
  const rowRefs = useRef({})

  useEffect(() => {
    if (!open || !files?.length) {
      startedRef.current = false
      return
    }
    stopRef.current = false
    setStopRequested(false)
    setPhase('idle')
    setCurrentIdx(-1)
    setRows(files.map(f => ({
      blob_path: f.blob_path,
      name: f.name,
      size: f.size,
      status: 'pending',
      data: null,
      error: null,
    })))
  }, [open, files])

  useEffect(() => {
    if (!open || !files?.length || startedRef.current) return undefined

    startedRef.current = true
    let cancelled = false
    stopRef.current = false
    setPhase('running')

    const run = async () => {
      const totals = { imported: 0, updated: 0, skipped: 0, errors: 0, files_done: 0, files_failed: 0 }

      for (let i = 0; i < files.length; i++) {
        if (cancelled || stopRef.current) {
          setRows(prev => prev.map((r, idx) => (idx >= i && r.status === 'pending'
            ? { ...r, status: 'stopped' }
            : r)))
          setPhase('stopped')
          onComplete?.({ ...totals, stopped: true, files_total: files.length, file_errors: buildFileErrors(rows) })
          return
        }

        setCurrentIdx(i)
        setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, status: 'running' } : r)))

        const file = files[i]
        try {
          const body = { blob_path: file.blob_path }
          if (tradingDay) body.trading_day = tradingDay
          if (templateId) body.template_id = parseInt(templateId, 10)
          if (folderPath) body.folder_path = folderPath
          if (venueMappings && Object.keys(venueMappings).length) {
            body.venue_mappings = venueMappings
          }

          const res = await authFetch(getLocalApiUrl(apiPath), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          })
          const json = await res.json()
          if (!json.success) throw new Error(json.error || 'Import failed')
          const d = json.data
          totals.imported += d.imported || 0
          totals.updated += d.updated || 0
          totals.skipped += d.skipped || 0
          totals.errors += d.errors || 0
          totals.files_done += 1
          if (d.errors > 0 && d.imported === 0) totals.files_failed += 1

          setRows(prev => prev.map((r, idx) => (idx === i
            ? { ...r, status: 'done', data: d, error: d.error_message || null }
            : r)))
        } catch (e) {
          totals.files_failed += 1
          totals.files_done += 1
          setRows(prev => prev.map((r, idx) => (idx === i
            ? { ...r, status: 'error', error: e.message }
            : r)))
        }
      }

      if (!cancelled) {
        setPhase('done')
        onComplete?.({ ...totals, stopped: false, files_total: files.length, file_errors: buildFileErrors(rows) })
      }
    }

    run()
    return () => { cancelled = true }
  }, [open, files, tradingDay, templateId, apiPath, folderPath, venueMappings, onComplete])

  useEffect(() => {
    if (currentIdx >= 0 && rowRefs.current[currentIdx]) {
      rowRefs.current[currentIdx].scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [currentIdx])

  const handleStop = () => {
    stopRef.current = true
    setStopRequested(true)
  }

  const handleClose = () => {
    if (phase === 'running') {
      stopRef.current = true
      setStopRequested(true)
      return
    }
    startedRef.current = false
    onClose()
  }

  if (!open) return null

  const doneCount = rows.filter(r => ['done', 'error'].includes(r.status)).length
  const okCount = rows.filter(r => r.status === 'done' && !(r.data?.errors > 0 && r.data?.imported === 0)).length
  const running = phase === 'running'
  const progressPct = files?.length ? Math.round((doneCount / files.length) * 100) : 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" aria-hidden />
      <div className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Batch ingest</h2>
            <p className="text-sm text-gray-600">
              {folderName || 'DimPatron'}
              {tradingDay && <> · {formatTradingDay(tradingDay)}</>}
              {' · '}{files?.length || 0} file(s) — one at a time
            </p>
          </div>
          <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {running && (
          <div className="px-6 py-3 bg-primary-50 border-b border-primary-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm text-primary-900">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
              <span>
                File {Math.min(currentIdx + 1, files.length)} of {files.length}
                {currentIdx >= 0 && rows[currentIdx] && (
                  <> — <span className="font-mono">{rows[currentIdx].name}</span></>
                )}
              </span>
            </div>
            <button
              type="button"
              onClick={handleStop}
              className="inline-flex items-center px-3 py-1.5 border border-red-300 text-red-700 rounded-md text-sm hover:bg-red-50"
            >
              <StopIcon className="w-4 h-4 mr-1" />
              Stop after current file
            </button>
          </div>
        )}

        {phase === 'stopped' && (
          <div className="px-6 py-2 bg-amber-50 border-b border-amber-100 text-sm text-amber-900">
            Stopped — {doneCount} of {files.length} files processed.
          </div>
        )}

        {phase === 'done' && (() => {
          const imported = rows.reduce((n, r) => n + (r.data?.imported || 0), 0)
          const errors = rows.reduce((n, r) => n + (r.data?.errors || 0), 0) + rows.filter(r => r.status === 'error').length
          const failed = rows.some(r => r.status === 'error' || (r.data?.errors > 0 && r.data?.imported === 0))
          const noneImported = imported === 0 && errors === 0
          const bannerCls = failed
            ? 'bg-red-50 border-red-100 text-red-900'
            : noneImported
              ? 'bg-amber-50 border-amber-100 text-amber-950'
              : 'bg-green-50 border-green-100 text-green-900'
          return (
            <div className={`px-6 py-2 border-b text-sm flex items-center gap-2 ${bannerCls}`}>
              <CheckCircleSolidIcon className={`w-5 h-5 flex-shrink-0 ${failed ? 'text-red-600' : noneImported ? 'text-amber-600' : 'text-green-600'}`} />
              Batch complete — {files.length} files, {imported} rows imported, {errors} errors
              {noneImported && ' (no new rows)'}
            </div>
          )
        })()}

        {rows.length > 0 && (
          <div className="px-6 py-3 border-b bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600 mb-2">
              <span className="flex items-center gap-3">
                <span><strong className="text-gray-900">{doneCount}</strong> / {files.length} processed</span>
                {okCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-green-700">
                    <CheckCircleSolidIcon className="w-3.5 h-3.5" />
                    {okCount} ok
                  </span>
                )}
              </span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden mb-3">
              <div
                className="h-full bg-green-500 transition-all duration-300 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
              {rows.map((row, i) => (
                <ProgressDot key={row.blob_path} row={row} index={i} />
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Green ✓ = processed · pulsing = current · grey = waiting
            </p>
          </div>
        )}

        <div className="px-6 py-4 overflow-y-auto flex-1">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8">#</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8" aria-label="Status icon" />
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Status</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase w-16">New</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase w-16">Sync</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase w-16">Skip</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase w-14">Err</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase w-16">ms</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, i) => (
                <React.Fragment key={row.blob_path}>
                  <tr
                    ref={(el) => { rowRefs.current[i] = el }}
                    className={
                    row.status === 'running' ? 'bg-primary-50 ring-1 ring-inset ring-primary-200'
                      : row.status === 'done' ? 'bg-green-50/40'
                        : row.status === 'error' ? 'bg-red-50'
                          : row.status === 'stopped' ? 'bg-gray-50 opacity-60'
                            : ''
                  }>
                    <td className="px-2 py-1.5 text-gray-400">{i + 1}</td>
                    <td className="px-2 py-1.5">
                      <FileStatusIcon row={row} />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs truncate max-w-[200px]" title={row.blob_path}>
                      {row.name}
                      <span className="text-gray-400 ml-1">({formatBytes(row.size)})</span>
                      {(row.data?.record_time_first || row.data?.record_time_last) && (
                        <div className="text-[10px] text-gray-500 font-sans mt-0.5 truncate" title={formatRecordTimeRange(row.data.record_time_first, row.data.record_time_last)}>
                          {formatRecordTimeRange(row.data.record_time_first, row.data.record_time_last)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                      <span className="inline-flex items-center gap-1">
                        {row.status === 'done' && <CheckCircleSolidIcon className="w-3.5 h-3.5 text-green-600" />}
                        {fileResultLabel(row)}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right">{row.data?.imported ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right">{row.data?.updated ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right">{row.data?.skipped ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right text-red-700">
                      {row.data?.errors > 0 ? (
                        <ImportErrorCount
                          count={row.data.errors}
                          onClick={() => onViewErrors?.({
                            title: `Errors — ${row.name}`,
                            filePath: row.blob_path,
                            errorCount: row.data.errors,
                            imported: row.data.imported,
                            updated: row.data.updated,
                            skipped: row.data.skipped,
                            errorDetails: parseImportErrorDetails(row.data),
                            error_message: row.error || row.data.error_message,
                          })}
                        />
                      ) : (row.error ? '!' : '—')}
                    </td>
                    <td className="px-2 py-1.5 text-right text-gray-500">{row.data?.duration_ms ?? '—'}</td>
                  </tr>
                  {(row.error || row.data?.error_message) && (
                    <tr className="bg-red-50/50">
                      <td colSpan={9} className="px-2 py-1 text-xs font-mono text-red-800 whitespace-pre-wrap">
                        {row.error || row.data?.error_message}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border rounded-md text-sm hover:bg-white"
          >
            {running ? (stopRequested ? 'Stopping…' : 'Stop after current file') : 'Close'}
          </button>
        </div>
      </div>
    </div>
  )
}
